import type {
  CreditV1ChangeSubscriptionReply,
  CreditV1ChangeSubscriptionRequest,
  CreditV1CreateOrderReply,
  CreditV1CreditsPurchaseProduct,
  CreditV1GetChangePreviewReply,
  CreditV1GetOrderStateReply,
  CreditV1OrderInfo,
  CreditV1PeriodProduct,
  SubscriptionsGetChangePreviewData,
} from '@/app/services/credit';

export type {
  CreditV1CreateOrderRequest,
  CreditV1CreditsPurchaseProduct,
  CreditV1PeriodProduct,
  CreditV1Subscription as SubscriptionType,
} from '@/app/services/credit';
export type { CreditV1GetOrderStateReply };

// 新版的套餐
export type PlanType = CreditV1PeriodProduct['plan_type'];
export type SubsPlanType = Exclude<
  CreditV1PeriodProduct['plan_type'],
  'unspecified' | 'standard' | 'advanced' | 'premium'
>;

export type PeriodType =
  | 'subscription_monthly'
  | 'subscription_quarterly'
  | 'subscription_yearly'
  | 'pass_monthly'
  | 'pass_quarterly'
  | 'pass_yearly';
export type ProductPayStatusType =
  | 'upgrade'
  | 'downgrade'
  | 'change'
  | 'unavailable'
  | 'available'
  | 'current'
  | 'continue'; // 升级、降级、变更（同级）、不可购买、可购买、当前购买、继续订阅
export type SubscriptionProductType = Omit<CreditV1PeriodProduct, 'plan_type'> & {
  product_pay_status: ProductPayStatusType;
  discount: number;
  plan_type: SubsPlanType;
  /**
   * 卡面划线价的展示基准（分），由前端按「按月购买原价」派生，仅在 use-fetch-product-list
   * 构建商品表时写入。与 original_price 解耦：original_price 表示商品自身原价，仍用于
   * 「下个月续费金额」等真实扣款文案；本字段只影响划线的对照展示。缺省时按 original_price 展示。
   */
  strikethrough_price?: number;
};

export type CreditsPurchaseProduct = CreditV1CreditsPurchaseProduct & {
  product_pay_status: ProductPayStatusType;
};

export type ClawPassProduct = Omit<CreditV1PeriodProduct, 'plan_type'> & {
  plan_type: SubsPlanType;
  product_pay_status: ProductPayStatusType;
};

export type NopeakPackProduct = CreditV1PeriodProduct & {
  product_pay_status: ProductPayStatusType;
};

// 产品变更相关
export type ChangePreview = SubscriptionsGetChangePreviewData['query'];

// 产品相关类型
export type ProductType = CreditsPurchaseProduct | SubscriptionProductType | ClawPassProduct | NopeakPackProduct;

// 注：team_v2 不在个人版 ProductList 模型里——企业版按 tier 单独建模，见下方 TeamProductList
type PlanProduct = SubscriptionProductType & {
  /** 同周期旗舰版的可选商品；主对象仍保留原来的默认商品。 */
  variants?: SubscriptionProductType[];
};
export type ProductPlan = Omit<
  Record<PlanType, PlanProduct | null>,
  'unspecified' | 'standard' | 'advanced' | 'premium' | 'team_v2'
>;

export type ProductList = Record<PeriodType, ProductPlan>;

/**
 * 企业版套餐分组：
 * - 与个人版 ProductList 结构对齐：`Record<TeamPeriodType, TeamProductPlan>`；
 * - 第一层按 billing_period（quarterly / yearly）拆开，命名空间用 'team_' 前缀
 *   避免和个人版 PeriodType（subscription_* / pass_*）冲突；
 * - 第二层按 tier（basic / plus / pro）索引，因为企业版所有 SKU 的 plan_type
 *   都是 'team_v2'，靠 tier 字段区分档位。
 */
export type TeamTier = 'basic' | 'plus' | 'pro';
export type TeamPeriodType = 'team_yearly' | 'team_quarterly';
export type TeamProductPlan = Record<TeamTier, SubscriptionProductType | null>;
export type TeamProductList = Record<TeamPeriodType, TeamProductPlan>;
export type TeamTabItemType = {
  value: TeamPeriodType;
  label: string;
  tooltip?: string;
  isDiscount?: boolean;
  discount?: number;
};

// 支付相关类型
export type PaymentInfoType = CreditV1CreateOrderReply | CreditV1ChangeSubscriptionReply;
export type OrderInfo = CreditV1OrderInfo & {
  plan_type: SubsPlanType;
};
export type PayStatustype =
  | CreditV1GetOrderStateReply['state']
  | 'loading'
  | 'failed'
  | 'subscriptionChangeExists'
  | 'paymentFailed';

// 确认变更信息视图信息
export type ChangePreviewViewDataType = CreditV1GetChangePreviewReply;

// 变更订阅请求
export type ChangeSubscriptionRequest = CreditV1ChangeSubscriptionRequest;

export type PlanCardStyle = {
  left: { transformOrigin: string; transform: string };
  right: { transformOrigin: string; transform: string };
};

export type PaidTrialInfo = {
  /** trial price in cents */
  price: number;
  /** trial duration in days */
  duration_days: number;
  /** regular monthly price in cents (first month after trial) */
  regular_price: number;
  /** currency code */
  currency: string;
};

export type TabItemType = {
  value: PeriodType;
  label: string;
  tooltip?: string;
  isDiscount?: boolean;
  discount?: number;
  /** Present only on the paid-trial tab */
  paidTrial?: PaidTrialInfo;
};

/** 付费弹窗的来源场景，决定标题/副标题文案与关闭时的挽留逻辑 */
export enum PlanDialogScene {
  /** 默认：普通升级入口，无副标题 */
  Default = 'default',
  /** 积分不足，无法发送任务 */
  InsufficientCredits = 'insufficientCredits',
  /** 去水印下载（需配合 creationId / withWatermark） */
  Download = 'download',
  /** Agent 项目 */
  AgentProject = 'agentProject',
}
